import { useImperativeHandle, useRef, useState } from "react";

import AdvancedSearchModal from "./AdvancedSearchModal";
import BasicTableSearchInput from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/BasicTableSearchInput";
import { GetDoc } from "@/Utils/StateLib";
import { GetFieldValueSearchCriteriaText } from "@/Utils/DataLib";
import { Flex, isNullish, ToolIcon, Button } from "@theorgchartdev/storybook";
import { setObject } from "@/Utils/Misc";
import { appstr } from "@coreapp/utils/appstr";

const CompositeTableSearchControls = ({
  UpdateSearchResultContext,
  sessionKeyContextId,
  showAllFieldNames,
  projectFields,
  holderFields = undefined,
  searchCtrlContext,
  prefixId,
  queryChartId,
  queryOptions,
  useProjectFieldDefs = false,
  disableRefetchOnSearchFocus = false,
  onSearchLoading = isLoading => {},
  processFullResult = false,
  hideAdvancedSearch = false,
  autoFocus = true
}) => {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [lastSearchCriteria, setLastSearchCriteria] = useState(null);

  const basicSearchInputCtrl = useRef(null);

  const doc = GetDoc();
  const docReady = doc != null;

  useImperativeHandle(searchCtrlContext, () => ({
    DoSearch,
    getQueryString: () => {
      return basicSearchInputCtrl.current?.getQueryString();
    },
    getQueryResults: () => {
      return basicSearchInputCtrl.current?.queryResults;
    },
    resetQuery: () => basicSearchInputCtrl.current?.resetQuery?.()
  }));

  const DoSearch = ({ queryOrCriteria, projectFields, queryOptions, holderProject } = {}) => {
    if (isNullish(queryOrCriteria) && hasAdvancedSearchFiltering()) {
      queryOrCriteria = lastSearchCriteria;
    }
    return basicSearchInputCtrl.current?.DoSearch({
      queryOrCriteria,
      projectFields,
      queryOptions,
      holderProject
    });
  };

  const openAdvancedSearch = () => {
    setShowAdvancedOptions(true);
  };

  const closeAdvancedSearch = () => {
    setShowAdvancedOptions(false);
  };

  const onAdvancedSearchResult = (searchResult, searchCriteria) => {
    setLastSearchCriteria(searchCriteria);
    basicSearchInputCtrl.current?.ClearSearchQuery();
    if (UpdateSearchResultContext) UpdateSearchResultContext(searchResult);
  };

  const resetAdvancedSearch = () => {
    setLastSearchCriteria(prevState => {
      if (isNullish(prevState)) {
        return null;
      }
      return setObject(prevState, null);
    });
  };

  const hasAdvancedSearchFiltering = () => {
    if (lastSearchCriteria == null) return false;
    return Object.values(lastSearchCriteria).some(value => {
      return !isNullish(value);
    });
  };

  const handleClearSearchFiltering = () => {
    resetAdvancedSearch();
    return DoSearch({ queryOrCriteria: "" });
  };

  const isAdvancedSearchActive = hasAdvancedSearchFiltering();
  let advancedSearchFilterText = lastSearchCriteria
    ? GetFieldValueSearchCriteriaText(lastSearchCriteria)
    : "";

  const isAdvancedSearchAvailable = (() => {
    if (hideAdvancedSearch) return false;
    if (showAllFieldNames) {
      return true;
    }

    return doc?.HasSearchKeys() ?? false;
  })();

  return (
    <Flex direction={"column"}>
      <Flex alignItems={"center"} justifyContent={"center"} gap={"8px"}>
        <BasicTableSearchInput
          ctrlRef={basicSearchInputCtrl}
          id={prefixId + "-search"}
          onSearchResult={UpdateSearchResultContext}
          processFullResult={processFullResult}
          autoFocus={autoFocus}
          projectFields={projectFields}
          holderFields={holderFields}
          queryChartId={queryChartId}
          launchFullResults={!isAdvancedSearchActive}
          disabled={isAdvancedSearchActive || !docReady}
          placeholder={advancedSearchFilterText}
          onResetInput={resetAdvancedSearch}
          dataTestId={`${prefixId}-search-input`}
          queryOptions={queryOptions}
          useProjectFieldDefs={useProjectFieldDefs}
          disableRefetchOnFocus={disableRefetchOnSearchFocus}
          onSearchLoading={onSearchLoading}
        />
        {(!docReady || isAdvancedSearchAvailable) && (
          <ToolIcon
            dataTestId={prefixId + "-advanced-search-button"}
            name={"sliders"}
            title={"Advanced"}
            onClick={openAdvancedSearch}
            tooltipProps={{ message: appstr("Advanced Search") }}
          />
        )}
        {isAdvancedSearchActive && (
          <Button
            dataTestId={prefixId + "-clear-search-filters-button"}
            modifier={"secondary"}
            iconProps={{ name: "broom-wide" }}
            onClick={handleClearSearchFiltering}
            size={"medium"}
          >
            {appstr("Clear search filters")}
          </Button>
        )}
      </Flex>
      <Flex flex={"1"} justifyContent={"center"}>
        {showAdvancedOptions && (
          <AdvancedSearchModal
            sessionKeyContextId={sessionKeyContextId}
            showAllFieldNames={showAllFieldNames}
            show={showAdvancedOptions}
            onClose={closeAdvancedSearch}
            displayResultsInContainer={false}
            onSearchResult={onAdvancedSearchResult}
            processFullResult={processFullResult}
            projectFields={projectFields}
            lastSearchCriteria={lastSearchCriteria}
            queryOptions={queryOptions}
            useProjectFieldDefs={useProjectFieldDefs}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default CompositeTableSearchControls;
